<template>
  <div id="wrapper">
    <LazyGlobalLoading v-if="loadingStore.show"/>
    <div v-else :key="loadingStore.show.toString()">
      <LazyLearningProSkillAssureHeader
          :is-in-progress="false"
          :title="myInfo.assessmentName"
          :organization-name="getOrgName"
      />
      <LazyLearningProSkillAssureStart
          :assessment-my-info="myInfo"
          :assessment-id="assessmentId"
          :device-blocked="isDeviceBlocked"
          @open-fail-popup="openFailPopup"
      />
      <LazyLearningProSkillAssureFooter/>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  LazyGlobalLoading,
  LazyLearningProSkillAssureFooter,
  LazyLearningProSkillAssureHeader,
  LazyLearningProSkillAssureStart,
  LearningConfirmPopupType,
} from "#components";
import {
  onBeforeMount,
  onMounted,
  ref,
  watch,
  useI18n,
  useRoute,
  useRouter,
} from "#imports";
import { computed } from "vue";
import { AxiosError } from "axios";

import { useUserStore } from "~/store/user/userStore";
import { useLearningStore } from "~/store/learning/learningStore";
import {useLearningAssessmentStore} from "~/store/learning/assessment/learningAssessmentStore";
import { useLoadingStore } from "~/store/loading/loadingStore";
import { useToastStore } from "~/store/toast/toastStore";

// TSK-11215: no sessionOwner meta here on purpose. This start page is the termination popup's
// own navigation destination (SessionOwnerGuard's leave() replaces here), and sessionOwnerScope()
// collapses this index route and the taking route onto ONE scope. Keeping the meta here would
// mean app.vue's :key never changes on arrival from the popup, so the guard would not remount —
// leaving the "displaced" state from the old screen stuck instead of clearing via stop()/release().

import { useProSkillAssureService } from "~/composables/service/useProSkillAssureService";
import { useUserService } from "~/composables/service/useUserService";
import { useLoadingTimeoutGuide } from "~/composables/useLoadingTimeoutGuide";

import {
  ProSkillAssureAssessmentMyInfoModel,
} from "~~/package/mapper/proSkillAssure/model/assessment/myInfo/ProSkillAssureAssessmentMyInfoModel";
import { RouteName } from "~~/package/enum/RouteName";
import { useGenericDialog } from "~/composables/useGenericDialog";
import { removeCookie, setCookie } from "~~/package/utils/CookieUtil";
import { CookieKeyType } from "~~/package/mapper/common/model/CookieKeyType";
import { TaskType } from "~~/package/mapper/learning/model/TaskType";
import { ToastType } from "~/components/Common/Toast/model/ToastType";
import { DeviceKind, getDeviceKind, isMobileOrTablet } from "~~/package/utils/DeviceUtil";
import {
  AssessmentEnvironmentEventType,
} from "~~/package/apis/ProSkillAssure/request/ProSkillAssureEnvironmentReportRequest";
import { setRedirectUrlCookie } from "~/utils/redirectUrlCookie";
import { clearExamAnswerDrafts } from "~/composables/examAnswerDraft";
import { ProSkillManagerAssessmentStatusType } from "~~/package/mapper/proSkillManager/model/assessment/ProSkillManagerAssessmentStatusType";
import { ProSkillManagerCandidateStatusType } from "~~/package/mapper/proSkillManager/model/candidate/ProSkillManagerCandidateStatusType";
import { SignType, SignUIType } from "~/components/Sign/enum/SignType";
import { useDialogStore } from "~~/application/store/dialog/dialogStore";
import DialogType from "~~/application/components/Common/Dialog/model/DialogType";

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const userStore = useUserStore();
const learningStore = useLearningStore();
    const assessmentStore = useLearningAssessmentStore();
const dialogStore = useDialogStore();
const loadingStore = useLoadingStore();
const toastStore = useToastStore();

const { openGenericAlert, openWrongApproachAlert } = useGenericDialog();

const assessmentId = ref(Number(route.params.assessmentId));
const myInfo = ref(new ProSkillAssureAssessmentMyInfoModel());

// 로딩이 20초 이상 지속되면(네트워크 단발 유실 등) 새로고침 안내 팝업 노출 (TSK-10923)
useLoadingTimeoutGuide(() => {
  dialogStore.open({
    type: DialogType.LearningConfirmPopup,
    props: { type: LearningConfirmPopupType.CONNECT_FAIL },
    confirm: { event: () => window.location.reload() },
  });
});

const getOrgName = computed(() =>
    myInfo.value.sampleAssessment ? "Codepresso" : myInfo.value.organizationName
);
const isRegistered = computed(
    () => String(route.query.isRegistered).toLowerCase() === "true"
);

const setRedirectCookies = () => setRedirectUrlCookie(route);

const goToHomePage = () => {
  router.replace({
    path: "/sign",
    query: {
      uiType: SignUIType.LOGIN,
      type: SignType.CANDIDATE
    }
  });
};

const openFailPopup = () => {
  openWrongApproachAlert();
};

/**
 * ASMT-CAND-19 (TSK-11687) — 모바일·태블릿 진입 차단.
 *
 * 판정은 규칙 동의보다 **먼저** 해야 한다. 늦으면 동의 팝업이 먼저 떠 버려서
 * "동의 전에 차단"이라는 기획 전제가 깨진다. 그래서 mount 직후 동기적으로 띄운다.
 *
 * 다만 평가 정보 조회까지 건너뛰면 안 된다 — myInfo 가 빈 모델로 남아 팝업 뒤에
 * "상태: 종료 / 1970년 1월 1일" 같은 깨진 화면이 비친다. 조회는 그대로 하고,
 * 조회가 끝난 뒤 열릴 수 있는 동의 팝업만 isDeviceBlocked 로 막는다
 * (dialogStore 는 단일 팝업이라 나중 open 이 차단 팝업을 덮어쓴다).
 *
 * 차단은 감독 유무와 무관한 전체 평가 공통 규칙이다.
 */
const isDeviceBlocked = ref(false);

/**
 * REQ-313 1차 QA — 운영이 특정 응시자에 한해 차단을 끌 수 있다(userMapCustomAssessment).
 *
 * 그 설정은 myInfo 에 실려 오므로 최종 판정은 조회가 끝난 뒤다. 조회 완료 → 로딩 해제 →
 * Start 컴포넌트 mount 순서라, 이 시점도 여전히 규칙 동의 팝업보다 앞선다(ASMT-CAND-19 전제 유지).
 */
const applyDeviceBlockPolicy = () => {
  if (getDeviceKind() === DeviceKind.DESKTOP) return;
  // 이 응시자만 차단 해제 — 모바일이어도 그대로 진행시킨다(웹훅도 보내지 않는다: 차단이 아니다).
  if (!myInfo.value.useDeviceBlockFlag) return;
  blockUnsupportedDevice();
};

const blockUnsupportedDevice = (): boolean => {
  const deviceKind = getDeviceKind();
  if (deviceKind === DeviceKind.DESKTOP) return false;

  isDeviceBlocked.value = true;

  // 알림은 부수 신호다 — await 하지 않고 차단 UI 를 즉시 띄운다.
  useProSkillAssureService().reportEnvironmentEvent(assessmentId.value, {
    eventType: AssessmentEnvironmentEventType.BLOCKED,
    deviceKind,
  });

  const leave = () => router.replace("/dashboard");
  openGenericAlert(
    {
      title: t("skillCertify-device-block-title"),
      contents: t("skillCertify-device-block-description"),
    },
    {
      confirm: { title: t("common-ok"), event: leave },
      close: { event: leave },
    }
  );
  return true;
};

// ASMT-CAND-04, ASMT-CAND-06: 응시 권한 없음(myInfo 403) 팝업 → /dashboard
const openNoPermissionPopup = () => {
  dialogStore.open({
    type: DialogType.LearningConfirmPopup,
    props: {
      icon: "no",
      title: t("skillCertify-limit-permission-title"),
      content: t("skillCertify-limit-permission-description"),
    },
    confirm: {
      title: t("common-ok"),
      event: () => router.replace("/dashboard"),
    },
    close: {
      event: () => router.replace("/dashboard"),
    },
  });
};

const openConnectFailPopup = () => {
  dialogStore.open({
    type: DialogType.LearningConfirmPopup,
    props: { type: LearningConfirmPopupType.CONNECT_FAIL },
    confirm: { event: () => {} },
  });
};

// ASMT-CAND-08: 녹화 실패(sessionStorage isRecordingIssue) 후 인트로 재진입 시 연결 실패 팝업
const checkRecordingIssue = () => {
  // ASMT-CAND-19: 차단 대상 디바이스면 차단 팝업만 띄운다 — 여기서 연결 실패 팝업을 먼저
  // 열면 차단 팝업이 그것을 덮어써 안내가 두 겹으로 겹친다.
  if (isMobileOrTablet()) return;
  const isRecordingIssue = sessionStorage.getItem("isRecordingIssue");
  if (isRecordingIssue === "true") {
    openConnectFailPopup();
    sessionStorage.removeItem("isRecordingIssue");
  }
};

const logoutAndGoToSign = async () => {
  // SEC-1 (TSK-10568): clear server-side HttpOnly cookie + legacy JS cookie.
  await useUserService().userSignOut();
  removeCookie(CookieKeyType.ACCESS_TOKEN);
  removeCookie(CookieKeyType.OPEN_CREDIT_BANNER);
  await goToSignInPage();
};

const goToRegisterInfoPage = () => {
  setRedirectCookies();
  router.replace({ path: "/register/info" });
};

// ASMT-CAND-04: 미로그인 → REDIRECT_URL 쿠키 저장 + /sign(LOGIN/SIGNUP은 isRegistered 분기)
const goToSignInPage = async () => {
  setRedirectCookies();

  if (!isRegistered.value) {
    setCookie(
      CookieKeyType.AUTH_ORG_EMAIL_SIGNUP_CONTEXT,
      { from: "auth-org-email", createdAt: Date.now() },
      { expires: new Date(Date.now() + 86400 * 1000), maxAge: 86400 }
    );
  }

  toastStore.openToast({
    toastType: ToastType.WARNING,
    contents: isRegistered.value ? t("account-required") : t("account-up-required"),
  });

  await router.replace({
    path: "/sign",
    query: {
      uiType: isRegistered.value ? SignUIType.LOGIN : SignUIType.SIGNUP,
      type: SignType.CANDIDATE,
      userEmail: route.query.userEmail
    },
  });

  useUserService().userLogOut();
};

const openProSkillAssureAccountMissMatch = () => {
  openGenericAlert(
    { title: t("skillCertify-account-miss-match"), contents: t("skillCertify-account-miss-match-description"), popupClass: 'dark' },
    { confirm: { title: t("settings-logout"), event: logoutAndGoToSign }, close: { event: logoutAndGoToSign } }
  );
};

const applyProctoringConfigFromMyInfo = () => {
  assessmentStore.proctoringConfig.useMultiMonitorProctor = myInfo.value.isMultiMonitorFlag;
  assessmentStore.proctoringConfig.useCopyPasteProctor = myInfo.value.isCopyPasteFlag;
  assessmentStore.proctoringConfig.useShortcutProctor = myInfo.value.isShortcutFlag;
  assessmentStore.proctoringConfig.useTabSwitchProctor = myInfo.value.isTabProctoring;
  assessmentStore.proctoringConfig.useScreenRecording = myInfo.value.isScreenRecordingFlag;
};

const getProSkillAssure = async () => {
  try {
    learningStore.resetProSkillAssure();

    const proSkillAssureData = await useProSkillAssureService().getProSkillAssureMyInfo(
        assessmentId.value
    );

    myInfo.value = proSkillAssureData;
    // #2260: answers kept across an expired-session login are useless once the assessment is over
    // (e.g. the server completed it at the deadline) — nothing would ever restore or delete them.
    if (proSkillAssureData.status === ProSkillManagerCandidateStatusType.COMPLETED
        || proSkillAssureData.assessmentStatus === ProSkillManagerAssessmentStatusType.ENDED) {
      clearExamAnswerDrafts(assessmentId.value);
    }
    applyProctoringConfigFromMyInfo();
    // 로딩이 닫히기 전(= Start 컴포넌트 mount 전)에 확정해야 동의 팝업보다 앞선다.
    applyDeviceBlockPolicy();

    learningStore.updateProSkillAssessmentInfo(
        assessmentId.value,
        assessmentStore.proSkillAssure.assessment,
        proSkillAssureData
    );

  } catch (e) {
    // 차단된 기기에서는 조회가 실패해도 에러 팝업을 열지 않는다 — dialogStore 가 단일 팝업이라
    // 차단 안내를 덮어써 버리고, 그러면 응시 불가 사유가 사라진 채 엉뚱한 안내만 남는다.
    if (isDeviceBlocked.value) return;

    const error = e as AxiosError;
    if (error.response?.status === 403) openNoPermissionPopup();
    else openFailPopup();
  } finally {
    // 성공·실패와 무관하게 반드시 닫는다. 열린 채로 두면 useLoadingTimeoutGuide 가 20초 뒤
    // 연결 실패 팝업을 띄워 역시 차단 안내를 덮어쓴다.
    loadingStore.closeLoading();
  }
};

onBeforeMount(() => {
  loadingStore.openLoading("");
  checkRecordingIssue();
});

/**
 * ASMT-CAND-04: 위치 미등록(→/register/info)·계정(userUUID) 불일치(→알럿+로그아웃) 확인. 둘 다 이 화면을 떠나게 하므로 차단 팝업과 경쟁하면 안 된다
 * — 차단 여부가 확정된 뒤에만 부른다.
 *
 * @returns 이동하거나 팝업을 띄웠으면 true(호출자는 뒤 흐름을 멈춘다).
 */
const leaveIfAccountNotReady = (): boolean => {
  if (!userStore.userV2.location) {
    goToRegisterInfoPage();
    return true;
  }

  if (route.query.userUUID && userStore.userV2.uuid !== route.query.userUUID) {
    openProSkillAssureAccountMissMatch();
    return true;
  }

  return false;
};

onMounted(async () => {
  // ASMT-CAND-19: 차단 판정과 팝업은 규칙·동의보다 앞선다. 단, 평가 정보 조회는 계속 진행해
  // 팝업 뒤 화면이 빈 모델(상태 '종료' / 1970-01-01)로 그려지지 않게 한다.

  // 차단 대상 기기가 아니면 종전대로 조회 전에 확인한다 — 등록 페이지로 보낼 응시자에게
  // 불필요한 조회를 시키지 않는다.
  if (!isMobileOrTablet()) {
    if (leaveIfAccountNotReady()) return;
    getProSkillAssure();
    return;
  }

  // 차단 대상 기기(모바일·태블릿)면 여기서 어디로도 보내지 않는다 — 안내 팝업이 대시보드로
  // 내보내는 유일한 출구여야 하고, navigate 하면 그 팝업이 그대로 사라진다. 실제 차단 여부는
  // 응시자 개별 설정(useDeviceBlockFlag)이 필요해 조회 이후(applyDeviceBlockPolicy)에 확정된다.
  //
  // 그래서 두 체크는 건너뛰는 것이 아니라 **보류**다(BR1 개정). 운영이 이 응시자의 차단을 꺼둬
  // 진입이 허용됐다면, 위치 미등록·계정 불일치는 데스크톱과 똑같이 걸러야 한다.
  await getProSkillAssure();
  if (isDeviceBlocked.value) return;
  leaveIfAccountNotReady();
});

watch(
    () => learningStore.isSaved,
    () => {
      if (learningStore.task.type !== TaskType.CODE_WITHOUT_RUN) return;
      if (learningStore.isSaved === null) return;

      toastStore.openToast({
        toastType: learningStore.isSaved ? ToastType.SUCCESS : ToastType.FAILED,
        contents: learningStore.isSaved ? t("learning-saved") : t("learning-save-failed"),
      });

      setTimeout(() => {
        learningStore.isSaved = null;
        toastStore.closeToast();
      }, 2000);
    }
);
</script>

<style>
@import "~/assets/scss/lecture_style.scss";
</style>
